import { useNavigate } from "react-router-dom";
import { History } from "lucide-react";
import styles from "./LatestActivities.module.css";
import BlankState from "../BlankState/BlankState";
import { timeAgo } from "../../../utils/moneyFormat";

const SKELETONS = [0, 1, 2, 3, 4];

// Booking activity as a chronological thread. Rows navigate by NUMERIC booking
// id — the reference form of the URL renders demo data instead of the record.
const LatestActivities = ({ activity, loading }) => {
  const navigate = useNavigate();
  const rows = Array.isArray(activity) ? activity : [];

  const open = (bookingId) => {
    if (bookingId) navigate(`/bookings/${bookingId}`);
  };

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div className={styles.headLeft}>
          <span className={`dshChip dshChipAccent ${styles.headChip}`}>
            <History size={14} strokeWidth={2.2} />
          </span>
          <div className={styles.headText}>
            <h3 className="dshTitle">Activity</h3>
            <p className="dshSub">Latest booking events</p>
          </div>
        </div>
      </div>

      <div className={`dshBody ${styles.body}`}>
        {loading && (
          <div className={styles.thread}>
            {SKELETONS.map((i) => (
              <div key={i} className={`dshRow ${styles.entry}`}>
                <span className={styles.rail} aria-hidden="true">
                  <i className={styles.dot} />
                </span>
                <div className={styles.content}>
                  <div className={styles.topLine}>
                    <span className={`dshSkel ${styles.skAction}`} />
                    <span className={`dshSkel ${styles.skTime}`} />
                  </div>
                  <span className={`dshSkel ${styles.skDesc}`} />
                  <span className={`dshSkel ${styles.skMeta}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && rows.length === 0 && (
          <BlankState
            icon={History}
            title="No booking activity yet"
            text="Confirmations, edits and cancellations are logged here as they happen."
          />
        )}

        {!loading && rows.length > 0 && (
          <div className={styles.thread}>
            {rows.map((it, idx) => {
              const clickable = Boolean(it.bookingId);
              return (
                <div
                  key={it.id != null ? it.id : idx}
                  className={`dshRow ${clickable ? "dshRowLink" : ""} ${styles.entry}`}
                  onClick={clickable ? () => open(it.bookingId) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            open(it.bookingId);
                          }
                        }
                      : undefined
                  }
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                >
                  <span className={styles.rail} aria-hidden="true">
                    <i
                      className={`${styles.dot} ${idx === 0 ? styles.dotLive : ""}`}
                    />
                  </span>

                  <div className={styles.content}>
                    <div className={styles.topLine}>
                      <span className={`dshTrunc ${styles.action}`}>
                        {it.action}
                      </span>
                      <span className={`dshNum ${styles.time}`}>
                        {timeAgo(it.at)}
                      </span>
                    </div>

                    {it.description ? (
                      <p className={styles.desc} title={it.description}>
                        {it.description}
                      </p>
                    ) : null}

                    <div className={styles.meta}>
                      {it.bookingReference ? (
                        <span className="dshRef dshNum">
                          {it.bookingReference}
                        </span>
                      ) : null}
                      {it.productType ? (
                        <span className="dshPill dshPillMute">
                          {it.productType}
                        </span>
                      ) : null}
                      {it.user ? (
                        <span className={`dshTrunc ${styles.user}`}>
                          {it.user}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LatestActivities;
